import React, { useEffect, useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
} from "chart.js";
import { useTranslation } from "react-i18next";

import { Typography, Spinner } from "stories/components";
import { Row, Col } from "stories/layout";
import { httpGetPrivateInvestimentFundDatas } from "services/private_investment_funds";
import { httpGetPrivateStockDatas } from "services/private_stocks";
import { httpGetInvestmentFundData } from "services/investment_fund";
import { roundScaleDecimals, thousandFormat } from "services/helpers";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

const readThemeCssVar = (selector, varName, fallback) => {
  if (typeof document === "undefined") return fallback;
  const el = selector ? document.querySelector(selector) : null;
  const node = el || document.documentElement;
  const value = getComputedStyle(node).getPropertyValue(varName).trim();
  return value || fallback;
};

const getPointDate = (item) => item?.date || item?.stock_date;
const getPointPrice = (item) => item?.updated_price ?? item?.price ?? item?.ending_price;

const normalizePoints = (items) => {
  if (!Array.isArray(items)) return [];

  return items
    .map((item) => ({
      date: getPointDate(item),
      price: Number(getPointPrice(item)),
    }))
    .filter((item) => item.date && !Number.isNaN(item.price))
    .sort((a, b) => new Date(a.date) - new Date(b.date));
};

const getChangeSinceDays = (points, days) => {
  if (!points.length) return null;

  const latest = points[points.length - 1];
  const targetTime = new Date(latest.date).getTime() - days * 24 * 60 * 60 * 1000;

  let baseline = points[0];
  for (let i = points.length - 1; i >= 0; i -= 1) {
    if (new Date(points[i].date).getTime() <= targetTime) {
      baseline = points[i];
      break;
    }
  }

  return latest.price - baseline.price;
};

const fetchSecurityRateHistory = (relatedModelType, securityId) => {
  const params = { ordering: "date", limit: 500, offset: 0 };

  if (relatedModelType === 30) {
    return httpGetPrivateInvestimentFundDatas({
      ...params,
      private_investment_fund: securityId,
    });
  }

  if (relatedModelType === 40) {
    return httpGetInvestmentFundData({
      ...params,
      investment_fund: securityId,
    });
  }

  if (relatedModelType === 10) {
    return httpGetPrivateStockDatas({
      ...params,
      private_stock: securityId,
    });
  }

  return Promise.resolve({ data: { results: [] } });
};

const SidebarSecurityValueChart = ({
  securityId,
  relatedModelType,
  currencyLabel,
  securityName,
  initialChartData = [],
  themeHostSelector = ".sidebar-security-chart",
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [points, setPoints] = useState([]);

  useEffect(() => {
    if (!securityId || !relatedModelType) {
      setPoints(normalizePoints(initialChartData));
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    fetchSecurityRateHistory(relatedModelType, securityId)
      .then((response) => {
        if (cancelled) return;
        const fetched = normalizePoints(response?.data?.results);
        setPoints(fetched.length ? fetched : normalizePoints(initialChartData));
      })
      .catch(() => {
        if (!cancelled) {
          setPoints(normalizePoints(initialChartData));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [securityId, relatedModelType]);

  const latestPrice = points.length ? points[points.length - 1].price : null;
  const monthlyChange = getChangeSinceDays(points, 30);
  const yearlyChange = getChangeSinceDays(points, 365);

  const chartConfig = useMemo(() => {
    const lineColor = readThemeCssVar(themeHostSelector, "--fp-chart-line", "#525f7f");
    const fillTop = readThemeCssVar(themeHostSelector, "--fp-chart-fill-top", "rgba(82, 95, 127, 0.35)");
    const fillBottom = readThemeCssVar(themeHostSelector, "--fp-chart-fill-bottom", "rgba(82, 95, 127, 0)");
    const gridColor = readThemeCssVar(themeHostSelector, "--fp-chart-grid", "rgba(136, 152, 170, 0.22)");

    const areaFill = (context) => {
      const chart = context.chart;
      const { ctx, chartArea } = chart;
      if (!chartArea) return fillTop;
      const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
      gradient.addColorStop(0, fillTop);
      gradient.addColorStop(1, fillBottom);
      return gradient;
    };

    return {
      data: {
        labels: points.map((item) =>
          new Date(item.date).toLocaleDateString(undefined, { day: "numeric", month: "short" })
        ),
        datasets: [
          {
            label: securityName || t("security", "Arvopaperi"),
            data: points.map((item) => item.price),
            borderColor: lineColor,
            backgroundColor: areaFill,
            fill: true,
            tension: 0.15,
            pointRadius: 0,
            borderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            grid: { display: false, drawBorder: false },
            ticks: {
              maxTicksLimit: 6,
              maxRotation: 0,
              minRotation: 0,
            },
          },
          y: {
            grid: { color: gridColor, drawBorder: false },
            ticks: {
              maxTicksLimit: 5,
            },
          },
        },
        plugins: {
          legend: { display: false },
          tooltip: { enabled: true },
        },
      },
    };
  }, [points, securityName, t, themeHostSelector]);

  const formatChange = (value) => {
    if (value == null || Number.isNaN(value)) return "-";
    const prefix = value >= 0 ? "+" : "";
    return `${prefix}${thousandFormat(roundScaleDecimals(value, 2))}`;
  };

  const changeClass = (value) => {
    if (value == null || Number.isNaN(value)) return "text-muted";
    return value >= 0 ? "text-success" : "text-danger";
  };

  if (loading) {
    return (
      <div className="sidebar-security-chart__loading d-flex justify-content-center py-4">
        <Spinner />
      </div>
    );
  }

  if (!points.length) {
    return (
      <Typography variant="medium" className="text-muted py-3">
        {t("sidebar_security_chart_no_data", "Ei kurssidataa. Lisää arvokehityskirjauksia nähdäksesi graafin.")}
      </Typography>
    );
  }

  return (
    <div className="sidebar-security-chart__body">
      <div className="sidebar-security-chart__value-block d-flex justify-content-between align-items-end mb-2">
        <Typography variant="h4" className="text-bold mb-0 text-truncate pr-2">
          {securityName}
        </Typography>
        <div className="text-right">
          <Typography variant="small" className="text-muted mb-0">
            {currencyLabel}
          </Typography>
          <Typography variant="h4" className="text-bold mb-0">
            {thousandFormat(roundScaleDecimals(latestPrice, 2))}
          </Typography>
        </div>
      </div>

      <div className="sidebar-security-chart__canvas mb-2">
        <Line data={chartConfig.data} options={chartConfig.options} />
      </div>

      <Row className="sidebar-security-chart__kpi border-top mx-0">
        <Col className="sidebar-security-chart__kpi-col px-2 py-2">
          <Typography variant="small" className="text-muted text-uppercase font-weight-bold mb-1 d-block">
            {t("frontpage_one_month", "1kk")}{" "}
            <span className="text-lowercase font-weight-normal">({currencyLabel})</span>
          </Typography>
          <Typography variant="h5" className={`text-bold mb-0 ${changeClass(monthlyChange)}`}>
            {formatChange(monthlyChange)}
          </Typography>
        </Col>
        <Col className="sidebar-security-chart__kpi-col border-left px-2 py-2">
          <Typography variant="small" className="text-muted text-uppercase font-weight-bold mb-1 d-block">
            {t("frontpage_one_year", "1v.")}{" "}
            <span className="text-lowercase font-weight-normal">({currencyLabel})</span>
          </Typography>
          <Typography variant="h5" className={`text-bold mb-0 ${changeClass(yearlyChange)}`}>
            {formatChange(yearlyChange)}
          </Typography>
        </Col>
      </Row>
    </div>
  );
};

export default SidebarSecurityValueChart;
